import type { Process } from '../type/Process';
import { processToFormValues } from './processToFormValues';

// All mappable keys (excluding materialId/articleId which are never pre-filled)
// machineId is excluded — it is always rendered in the right column of the material/machine row
export const ALL_MAPPABLE_KEYS = [
  'operator', 'operation', 'ae', 'ap', 'vc', 'fz',
  'volume', 'distance', 'pieces', 'duration_total', 'duration_piece', 'malrond',
  'lubType', 'lubBrand', 'holderType', 'holderBrand', 'clampingType', 'clampingBrand',
] as const;

export type MappableKey = typeof ALL_MAPPABLE_KEYS[number];

// Keys computed at submit — never shown to the operator
const ALWAYS_HIDDEN = new Set(['operator', 'duration_total', 'volume', 'distance', 'duration_piece']);

// Always shown regardless of process pre-fill
const ALWAYS_VISIBLE = new Set(['pieces']);

export function computeVisibleKeys(process: Process): Set<string> {
  const processFormValues = processToFormValues(process);
  const visibleKeys = new Set<string>();
  for (const key of ALL_MAPPABLE_KEYS as readonly MappableKey[]) {
    if (ALWAYS_HIDDEN.has(key)) continue;
    if (ALWAYS_VISIBLE.has(key)) { visibleKeys.add(key); continue; }
    if (process.mandatoryFields.length > 0) {
      // Explicit allow-list from checkboxes
      if (process.mandatoryFields.includes(key)) visibleKeys.add(key);
    } else {
      // Legacy fallback: show if null (empty mandatoryFields = old process)
      if (!(key in processFormValues)) visibleKeys.add(key);
    }
  }
  // The backend requires at least one machining duration. duration_* is normally
  // computed from the process's per-piece rate, but a process saved without any
  // duration would submit null → 400 — so ask the operator instead.
  if (process.pieceMachiningDuration === null && process.totalMachiningDuration === null) {
    visibleKeys.add('duration_piece');
  }
  return visibleKeys;
}

// Form key → i18n label key, so validation errors can NAME missing fields with the
// exact label the operator sees on the input (machine lives in its own column).
const FIELD_LABEL_KEYS: Record<string, string> = {
  operator: 'traceability.processManager.form.prefillFields.operator.label',
  operation: 'traceability.processManager.form.prefillFields.operation.label',
  ae: 'traceability.processManager.form.prefillFields.ae.label',
  ap: 'traceability.processManager.form.prefillFields.ap.label',
  vc: 'traceability.processManager.form.prefillFields.vc.label',
  fz: 'traceability.processManager.form.prefillFields.fz.label',
  volume: 'traceability.processManager.form.prefillFields.machinedVolume.label',
  distance: 'traceability.processManager.form.prefillFields.machinedDistance.label',
  pieces: 'traceability.processManager.form.prefillFields.machinedPieces.label',
  duration_total: 'traceability.processManager.form.prefillFields.totalMachiningDuration.label',
  duration_piece: 'traceability.processManager.form.prefillFields.pieceMachiningDuration.label',
  malrond: 'traceability.processManager.form.prefillFields.malround.label',
  lubType: 'traceability.processManager.form.prefillFields.lubType.label',
  lubBrand: 'traceability.processManager.form.prefillFields.lubBrand.label',
  holderType: 'traceability.processManager.form.prefillFields.holderType.label',
  holderBrand: 'traceability.processManager.form.prefillFields.holderBrand.label',
  clampingType: 'traceability.processManager.form.prefillFields.clampingType.label',
  clampingBrand: 'traceability.processManager.form.prefillFields.clampingBrand.label',
  machine: 'traceability.processSelection.sections.machine',
};

export function fieldLabelKey(key: string): string {
  return FIELD_LABEL_KEYS[key] ?? key;
}

export function computePerPieceValues(
  process: Process,
  pieces: number
): Record<string, number | null> {
  return {
    volume: process.machinedVolume !== null ? process.machinedVolume * pieces : null,
    distance: process.machinedDistance !== null ? process.machinedDistance * pieces : null,
    duration_total: process.pieceMachiningDuration !== null ? process.pieceMachiningDuration * pieces : null,
  };
}
